import type { IconifyIcon } from '@iconify/types'
import type { JSX } from 'solid-js'
import { type Component, createMemo, createSignal, createUniqueId, mergeProps, Show, splitProps } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import Button from '~/components/Button'
import FieldMessage from '~/components/FieldMessage'
import Icon from '~/components/Icon'
import { useFieldError } from '~/hooks/useFieldError'
import type { MainSizesType } from '~/types/components'
import type { IconPropType, InputPropsType } from '~/types/Input'
import { controlSize, floatingLabelClasses } from '~/utils/controlSize'

const isIconifyIcon = (icon: unknown): icon is IconifyIcon => typeof icon === 'object' && icon !== null && 'body' in icon

const isInteractiveElement = (element: unknown): element is JSX.Element => {
  if (!element || typeof element === 'string') return false
  // Icono de Iconify (objeto con body) → pasivo
  if (typeof element === 'object' && 'body' in element) return false
  // Todo lo demás (componente JSX, nodo DOM, accessor memo de Solid) → interactivo
  return true
}

const Input: Component<InputPropsType> = props => {
  const uid = createUniqueId()
  const [inputType, setInputType] = createSignal<string>(props.type ?? 'text')
  const merged = mergeProps(
    {
      variant: 'default',
      size: 'md' as MainSizesType
    },
    props
  )
  const [local, inputProps] = splitProps(merged, [
    'label',
    'ref',
    'type',
    'size',
    'class',
    'placeholder',
    'id',
    'autocomplete',
    'startIcon',
    'endIcon',
    'prependIcon',
    'appendIcon',
    'variant',
    'error',
    'helperText'
  ])

  const id = () => local.id ?? uid

  // Sin prop `error`, el campo lee su error del contexto del `<Form>` (si el
  // padre se lo publica por su `name`). La prop explícita gana sobre el contexto.
  const resolvedError = useFieldError(
    () => (inputProps.name ? String(inputProps.name) : undefined),
    () => local.error
  )

  const getAutocompleteValue = () => props.autocomplete || (props.type === 'email' ? 'email' : props.type === 'password' ? 'current-password' : undefined)

  const sizeConfig = createMemo(() => controlSize(local.size))

  const getAdornmentClasses = (isStart: boolean, isInteractive: boolean) => {
    const base = 'absolute inset-y-0 flex items-center'
    const position = isStart ? 'start-0' : 'end-0'
    const padding = isInteractive ? (isStart ? 'ps-1.5' : 'pe-1.5') : isStart ? 'ps-3.5' : 'pe-2.5'
    const color = isInteractive ? '' : 'text-foreground-subtle'
    // Ensure the adornment sits above the input and doesn't get hidden
    const z = isInteractive ? 'z-20' : 'z-10 pointer-events-none'
    // Un botón incrustado se queda por debajo del alto del campo: si mide lo
    // mismo, llena el hueco de borde a borde y parece que se sale.
    const child = isInteractive ? sizeConfig().adornmentChild : ''
    return twMerge(base, position, padding, color, z, child)
  }
  const renderIcon = (icon: IconPropType | undefined, isStart: boolean): JSX.Element | null => {
    if (!icon) return null

    if (isInteractiveElement(icon)) {
      return <div class={getAdornmentClasses(isStart, true)}>{icon}</div>
    }
    if (typeof icon === 'string' || isIconifyIcon(icon)) {
      return (
        <div class={getAdornmentClasses(isStart, false)}>
          <Icon icon={icon} width={sizeConfig().icon} class="opacity-90" />
        </div>
      )
    }
    return <div class={getAdornmentClasses(isStart, false)}>{icon}</div>
  }

  const hasError = () => Boolean(resolvedError())
  const getVariantClasses = createMemo(() => {
    return twMerge(
      'focus:outline-none focus:ring-0',
      'focus:border-primary',
      'bg-transparent',
      local.variant === 'text' ? 'border-b-2' : '',
      local.variant === 'outlined' ? 'border-2' : '',
      ['text', 'outlined'].includes(local.variant ?? 'default') && hasError() ? 'border-danger' : 'border-border-strong',
      local.variant === 'default' ? 'border bg-surface-muted' : '',
      local.variant === 'default' && hasError() ? 'border border-danger' : '',
      hasError() ? 'focus:border-danger' : ''
    )
  })

  const hasStartAdornment = () => local.startIcon || local.prependIcon
  const hasEndAdornment = () => local.endIcon || local.appendIcon || local.type === 'time' || local.type === 'password'

  const inputClasses = createMemo(() =>
    twMerge(
      getVariantClasses(),
      // La altura es fija (ver `utils/controlSize`): con etiqueta, el relleno
      // sólo empuja el texto hacia abajo para dejarle sitio arriba.
      'block rounded-lg duration-100 w-full text-slate-900 appearance-none dark:text-white peer',
      sizeConfig().padX,
      hasStartAdornment() ? 'ps-12' : '',
      hasEndAdornment() ? 'pe-11' : '',
      local.label ? sizeConfig().labelPad : 'py-0',
      // Con etiqueta flotante el placeholder estorba: en reposo la etiqueta está
      // justo encima del texto y los dos se leerían a la vez. Aparece al enfocar,
      // que es cuando la etiqueta ya ha subido.
      local.label && local.placeholder ? 'placeholder:opacity-0 focus:placeholder:opacity-100 placeholder:transition-opacity' : '',
      sizeConfig().text,
      sizeConfig().height,
      local.class || ''
    )
  )

  const labelClasses = createMemo(() =>
    twMerge(
      floatingLabelClasses(local.size),
      'max-w-[calc(100%-1rem)]',
      hasStartAdornment() ? 'start-12' : sizeConfig().padStart,
      hasError() ? 'text-danger peer-placeholder-shown:text-danger' : 'text-foreground-muted peer-placeholder-shown:text-foreground peer-focus:text-slate-800 peer-focus:dark:text-white'
    )
  )

  return (
    <div class="relative flex w-full flex-col">
      <div class="relative">
        {/* Start adornment (icon before input) */}
        <Show when={local.startIcon || local.prependIcon}>{renderIcon(local.startIcon || local.prependIcon, true)}</Show>

        <input
          class={inputClasses()}
          {...inputProps}
          autocomplete={getAutocompleteValue()}
          // La etiqueta flotante se apoya en `peer-placeholder-shown`, que sólo
          // marca el campo vacío **si tiene placeholder**: de ahí el espacio.
          placeholder={local.placeholder || ' '}
          type={inputType()}
          id={id()}
          ref={el => {
            if (typeof local.ref === 'function') local.ref(el)
          }}
          aria-label={local.label}
          aria-describedby={`${id()}-message`}
        />

        <Show when={local.endIcon || local.appendIcon}>{renderIcon(local.endIcon || local.appendIcon, false)}</Show>
        <Show when={local.type === 'password'}>
          <div class={twMerge('absolute inset-y-0 end-0 flex items-center pe-1.5 text-foreground-subtle z-20', sizeConfig().adornmentChild)}>
            <Button
              icon={inputType() === 'password' ? 'mdi:eye-off' : 'material-symbols-light:visibility-outline'}
              iconSize={sizeConfig().icon}
              size={local.size}
              variant="text"
              aria-label={inputType() === 'password' ? 'Mostrar contraseña' : 'Ocultar contraseña'}
              tooltip={inputType() === 'password' ? 'Mostrar contraseña' : 'Ocultar contraseña'}
              aria-controls={id()}
              onClick={() => {
                setInputType(prev => (prev === 'password' ? 'text' : 'password'))
              }}
            />
          </div>
        </Show>

        <Show when={local.label}>
          <label for={id()} class={labelClasses()}>
            {local.label}
          </label>
        </Show>
      </div>

      <FieldMessage id={`${id()}-message`} size={local.size} error={resolvedError()} helperText={local.helperText} class={hasStartAdornment() ? 'ps-12' : ''} />
    </div>
  )
}

export default Input
